import type { BuildMarker, GameDevStage } from '@playerbase/types';

import type { BuildMarkerSummary, BuildStageSummary } from './playtest-sessions-data.js';

// Resolve "which build was live at time T" from a game's marker timeline.
//
// Playtest sessions answer this at creation time by stamping a
// build_marker_id on the row (the bot's ingest RPC picks the current
// marker - see migration 0065). Feedback, form submissions and pattern
// mentions carry no such column, so we join in memory instead: the shell
// already loads the active marker set + dev stages for the current game,
// and the set is small. This mirrors the RPC's ordering -
// coalesce(released_at, created_at) desc, created_at desc - so a build
// resolved here matches what a session would have recorded at the same
// instant.
//
// Comparisons go through Date.parse rather than string ordering: marker
// timestamps and the feedback timestamps we test against can carry
// different UTC offsets, which breaks lexical comparison.

export type { BuildMarkerSummary, BuildStageSummary };

// The instant a marker "took effect" - its release time, falling back to
// when it was created. This is the same key buildActiveAt orders by and
// the timestamp a "build changed" event should sort against.
export function markerEffectiveAt(marker: BuildMarker): string {
  return marker.releasedAt ?? marker.createdAt;
}

// Project a full marker onto the compact summary the render path expects,
// resolving its dev stage from the game's stage list.
export function toBuildMarkerSummary(
  marker: BuildMarker,
  devStages: GameDevStage[],
): BuildMarkerSummary {
  const stage = devStages.find((s) => s.id === marker.stageId) ?? null;
  return {
    id: marker.id,
    label: marker.label,
    version: marker.version,
    stage:
      stage === null
        ? null
        : ({ id: stage.id, label: stage.label, colour: stage.colour } satisfies BuildStageSummary),
  };
}

export interface BuildChange {
  marker: BuildMarkerSummary;
  // The marker's effective time (released_at, falling back to created_at).
  changedAt: string;
}

// Every marker projected to a build change, newest first. Unlike
// buildChangesWithin this isn't time-windowed - it's the full set the
// feeds interleave divider rows from (interleaveBuildMarkers drops the
// ones that fall outside the loaded range).
export function toBuildChanges(
  markers: BuildMarker[],
  devStages: GameDevStage[],
): BuildChange[] {
  return markers
    .map((marker) => ({
      marker: toBuildMarkerSummary(marker, devStages),
      changedAt: markerEffectiveAt(marker),
    }))
    .sort((a, b) => Date.parse(b.changedAt) - Date.parse(a.changedAt));
}

// Builds that went live within `windowMs` before `nowMs`, newest first.
// Drives the "build changed" entries in the feedback activity feed. Future
// dated releases (changedAt > nowMs) haven't happened yet, so they're
// excluded - a build "changes" when it goes live, not when it's scheduled.
export function buildChangesWithin(
  markers: BuildMarker[],
  devStages: GameDevStage[],
  nowMs: number,
  windowMs: number,
): BuildChange[] {
  const out: BuildChange[] = [];
  for (const marker of markers) {
    const changedAt = markerEffectiveAt(marker);
    const ts = Date.parse(changedAt);
    if (Number.isNaN(ts) || ts > nowMs || nowMs - ts > windowMs) continue;
    out.push({ marker: toBuildMarkerSummary(marker, devStages), changedAt });
  }
  out.sort((a, b) => b.changedAt.localeCompare(a.changedAt));
  return out;
}

// The marker with the greatest effective time (released_at, falling back
// to created_at) that is at or before `timestamp`. Returns null when no
// build precedes the timestamp - the caller renders "Unregistered build",
// matching the playtest session treatment for a null marker.
export function buildActiveAt(
  markers: BuildMarker[],
  devStages: GameDevStage[],
  timestamp: string,
): BuildMarkerSummary | null {
  const target = Date.parse(timestamp);
  if (Number.isNaN(target)) return null;

  let best: BuildMarker | null = null;
  let bestEffective = Number.NEGATIVE_INFINITY;
  let bestCreated = Number.NEGATIVE_INFINITY;
  for (const marker of markers) {
    const effective = Date.parse(markerEffectiveAt(marker));
    if (Number.isNaN(effective) || effective > target) continue;
    const created = Date.parse(marker.createdAt);
    const createdRank = Number.isNaN(created) ? Number.NEGATIVE_INFINITY : created;
    if (
      best === null ||
      effective > bestEffective ||
      (effective === bestEffective && createdRank > bestCreated)
    ) {
      best = marker;
      bestEffective = effective;
      bestCreated = createdRank;
    }
  }
  return best === null ? null : toBuildMarkerSummary(best, devStages);
}
